import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';

import { DataPrivacyStatement } from '@/components/DataPrivacyStatement';
import { NavigationBar } from '@/components/NavigationBar';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackContactInfoCollected } from '@/lib/analytics';
import {
  type ForSomeoneElseFormData,
  forSomeoneElseFormId,
  forSomeoneElseFormSchema,
} from '@/types/user-profile.type';

export const ForSomeoneElse = () => {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const { representativeFirstName, representativeLastName, contactPhoneNumber, contactEmail } =
    useSessionStore();

  const form = useForm<ForSomeoneElseFormData>({
    resolver: zodResolver(forSomeoneElseFormSchema),
    defaultValues: {
      firstName: representativeFirstName,
      lastName: representativeLastName,
      phoneNumber: contactPhoneNumber,
      email: contactEmail,
    },
  });

  const onSubmit = (data: ForSomeoneElseFormData) => {
    const { updateState } = useSessionStore.getState();

    updateState({
      representativeFirstName: data.firstName,
      representativeLastName: data.lastName,
      contactPhoneNumber: data.phoneNumber,
      contactEmail: data.email,
    });

    trackContactInfoCollected({
      primaryContactEmail: data.email,
      primaryContactPhoneNumber: data.phoneNumber,
      primaryContactPhoneNumberVerified: false,
      patientRecipient: 'someone-else',
    });

    // Auto-save session (non-blocking)
    saveSession();

    navigate({ to: '/create-profile/for-someone-else/relationship' });
  };

  const handleBack = () => {
    navigate({ to: '/insurance/benefits' });
  };

  return (
    <section>
      <NavigationBar onBack={handleBack} backLabel="Results" />
      <Card>
        <CardHeader>
          <CardTitle>First, let's get your contact info</CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={form.handleSubmit(onSubmit)} id={forSomeoneElseFormId}>
            <div className="space-y-8 sm:space-y-12">
              <FieldGroup>
                <div className="space-y-7">
                  <Controller
                    name="firstName"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const inputId = `${forSomeoneElseFormId}-firstName`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={inputId}>Your first name</FieldLabel>
                          <Input
                            {...field}
                            id={inputId}
                            type="text"
                            aria-invalid={fieldState.invalid}
                            placeholder="First name"
                          />
                          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name="lastName"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const inputId = `${forSomeoneElseFormId}-lastName`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={inputId}>Your last name</FieldLabel>
                          <Input
                            {...field}
                            id={inputId}
                            type="text"
                            aria-invalid={fieldState.invalid}
                            placeholder="Last name"
                          />
                          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name="phoneNumber"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const inputId = `${forSomeoneElseFormId}-phoneNumber`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={inputId}>Your mobile phone</FieldLabel>
                          <PhoneNumberInput
                            {...field}
                            id={inputId}
                            aria-invalid={fieldState.invalid}
                          />
                          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      );
                    }}
                  />

                  <Controller
                    name="email"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      const inputId = `${forSomeoneElseFormId}-email`;

                      return (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor={inputId}>Your email</FieldLabel>
                          <Input
                            {...field}
                            id={inputId}
                            type="email"
                            aria-invalid={fieldState.invalid}
                            placeholder="name@youremail.com"
                            inputMode="email"
                          />
                          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                        </Field>
                      );
                    }}
                  />
                </div>
              </FieldGroup>

              <p className="text-charcoal-400 text-sm leading-[20px]">
                By clicking continue below, you acknowledge that you have read, understood, and
                accepted the Conduit Consent and Conduit Communication policies.
              </p>

              <Button type="submit" className="w-full sm:w-auto sm:min-w-[186px]">
                Continue
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <DataPrivacyStatement />
    </section>
  );
};
